import { Link, useLocation, useResolvedPath } from 'react-router-dom';
import {
  PageLayout,
  PageLayoutProps,
  PageLayoutTab,
} from '@backstage/frontend-plugin-api';
import { SwappableComponentBlueprint } from '@backstage/plugin-app-react';
import { makeStyles, Typography } from '@material-ui/core';
import { boschBlue, boschGray, boschWhite } from './boschColors';

/**
 * Tiny global stylesheet that BoschPageLayout injects once. We keep it
 * here (instead of in `theme/index.tsx`) so the override lives next to
 * the layout it visually belongs to — these rules patch the chrome of
 * pages that the layout renders around.
 *
 * Selector pattern: BUI (`@backstage/ui`) ships its CSS as CSS Modules
 * with a build-time hash suffix (e.g.
 * `Header_bui-HeaderBreadcrumbs__6fa2a92baa`), so the full class name
 * isn't stable across plugin upgrades — but the `bui-Header*` prefix
 * is. Hence the attribute-substring selectors below
 * (`[class*="bui-HeaderBreadcrumbs"]`).
 *
 * What this currently fixes:
 *
 *   - The TechDocs reader's title row (`bui-HeaderContent` from
 *     `Header.module.css` in `@backstage/ui`) lays out title vs
 *     search/source-link controls with `justify-content: space-between`,
 *     but the breadcrumbs+title block on the left has no `flex-grow`
 *     and so collapses to its intrinsic width. The visual result is
 *     the title floating in the middle of the row right next to the
 *     search box, instead of being pushed to the page edge. Forcing
 *     the breadcrumbs block to grow with `flex: 1 1 auto` fixes this
 *     without touching anything else.
 *
 *   - The same `bui-HeaderContent` row has no `gap` defined in
 *     `Header.module.css`, so once the breadcrumbs block grows to
 *     fill the available width (per the rule above) it ends up
 *     literally touching the right-hand controls — the page title
 *     sits flush against the "Search docs" input with no breathing
 *     room. We add `gap: 32px` to guarantee a horizontal gutter.
 *
 *   - Backstage's BUI Header also has no `padding-bottom`, so the
 *     entity-card row stacks directly under the title row. We add a
 *     small `padding-bottom` so the System / Owner badges are
 *     visually separated from the title above them — same vibe the
 *     stock `<Header>` from `@backstage/core-components` ships.
 *
 * ⚠️ DO NOT add broad `[class*="bui-Container"]` rules here. The BUI
 * Container is reused INSIDE the docs reader (around the entity
 * card / subheader / docs body via `<Container mt="6">` in
 * `TechDocsReaderLayout.esm.js`) — overriding its margins broke the
 * docs nav sidebar layout (it ended up overlapping the entity
 * metadata strip). If you want to tighten Container spacing on
 * specific pages, scope the rule with a path-aware ancestor (e.g.
 * `.bosch-page-docs [class*="bui-Container"]`) instead.
 */
const useGlobalChromeStyles = makeStyles(
  () => ({
    '@global': {
      '[class*="bui-HeaderContent"]': {
        gap: 32,
      },
      '[class*="bui-HeaderBreadcrumbs"]': {
        flex: '1 1 auto',
        minWidth: 0,
      },
      '[class*="bui-Header__"]': {
        paddingBottom: 16,
      },
    },
  }),
  { name: 'BoschGlobalChrome' },
);

/**
 * URL prefixes for pages whose `loader` already renders a header (either
 * `<Header>` from `@backstage/core-components` or `<HeaderPage>` from
 * `@backstage/ui`). For these we suppress our own banner so users don't
 * see two stacked title bars.
 *
 * Why each entry is here:
 *
 *   - /rbac        → 3rd-party plugin (`@backstage-community/plugin-rbac`)
 *                    that renders `<Page><Header/></Page>` internally.
 *   - /catalog     → Built-in catalog plugin's `NfsBaseCatalogPage` uses
 *                    `<HeaderPage title="Bosch Digital Factory Catalog"
 *                     customActions={...} />` from `@backstage/ui`.
 *                    Without this entry the catalog page shows BOTH
 *                    "Catalog" (our banner) AND "Bosch Digital Factory
 *                    Catalog" (the plugin's BUI HeaderPage), with the
 *                    Create button looking centred and squashed.
 *   - /api-docs    → Built-in api-docs plugin's `NfsApiExplorerPage`
 *                    uses the same `<HeaderPage>` pattern as catalog,
 *                    showing "Bosch Digital Factory API Explorer" with
 *                    a Register button.
 *
 * If you install another third-party plugin that renders its own header
 * (and you can't pass `noHeader: true` because you don't own the
 * `PageBlueprint`), add the URL prefix here.
 */
const PAGES_WITH_OWN_HEADER: readonly string[] = [
  /*
   * Dynamic admin-authored pages — `DynamicContentPage` renders its
   * own `<Header>` from `core-components` so it can pull the title and
   * subtitle from the matching `ContentPage` entity's annotations
   * (`bosch.io/content-page-banner-title` /
   * `bosch.io/content-page-banner-subtitle`). Without this entry the
   * `BoschPageLayout` banner would stack on top of the entity-driven
   * header — same problem `/rbac` had before the suppression CSS rule.
   *
   * The prefix-match (`pathname.startsWith('/p/')`) below means the
   * rule applies to every `/p/<slug>` URL automatically — no need to
   * keep this list in sync with the actual content pages.
   */
  '/p',
];

/**
 * Per-route banner subtitle and (optional) display title overrides.
 *
 * Why we maintain this map manually:
 *   - The framework's `PageLayout` props only expose `title`, not
 *     `subtitle`. Plugins like `notifications` even hand us their
 *     pluginId as the title (so the banner reads "notifications"
 *     in lowercase), which is ugly. Mapping the URL gives us a single
 *     place to fix both the casing and supply the explanatory phrase.
 *   - The user's design language (matching `KindsGuidePage`) is
 *     "title in h3 + a one-line subtitle in subtitle2 underneath".
 *     A subtitle map is the lightest way to ship that for every page
 *     without forking each plugin's `PageBlueprint`.
 *
 * Match is exact-then-prefix on the URL. Add a new entry whenever you
 * install a plugin that exposes a top-level page and you'd like a
 * branded banner subtitle for it. Pages that are NOT in the map fall
 * back to "title only" — no subtitle row is rendered, banner stays
 * compact (e.g. tab sub-routes under /create like /create/templates
 * inherit the same title from the parent layout).
 *
 * Pages already in `PAGES_WITH_OWN_HEADER` ignore this map (their
 * own `<Header>` / `<HeaderPage>` controls the chrome).
 */
const PAGE_OVERRIDES: Record<
  string,
  { title?: string; subtitle?: string }
> = {
  '/catalog': {
    title: 'Catalog',
    subtitle:
      'Browse the Digital Factory catalog',
  },
  '/api-docs': {
    title: 'API Docs',
    subtitle:
      'Browse the Digital Factory API Docs',
  },
  '/docs': {
    title: 'Documentation',
    subtitle:
      'Searchable TechDocs published by every component in the Digital Factory catalog',
  },
  '/notifications': {
    title: 'Notifications',
    subtitle:
      'Heads-up messages routed to you by Backstage plugins and integrations',
  },
  '/settings': {
    title: 'Settings',
    subtitle:
      'Tweak your profile, themes, language and connected auth providers',
  },
  '/create': {
    title: 'Create',
    subtitle:
      'Spin up a new component, API, resource or doc from a vetted template',
  },
  '/search': {
    title: 'Search',
    subtitle:
      'Find anything indexed across the catalog, TechDocs and registered APIs',
  },
  '/permissions-guide': {
    subtitle:
      'Cheat-sheet for the live editor at /rbac — bootstrap defaults, role matrix and per-role detail',
  },
  '/kinds-guide': {
    subtitle:
      'Live snapshot of our Digital Factory plus a reference for every kind',
  },
  '/rbac': {
    title: 'RBAC',
    subtitle:
      'Role-based access control for the Digital Factory',
  },
  '/admin': {
    title: 'Governance',
    subtitle:
      'Unified hub for users, groups, roles, permissions, and notifications',
  },
};

/**
 * URL prefixes whose loader content does NOT wrap itself in `<Content>`
 * from `@backstage/core-components` (which would supply a 24px gutter)
 * AND does NOT have its own internal padding either. We add the gutter
 * ourselves for these so tables/cards don't touch the page edges.
 *
 * Currently empty: /docs USED to need this (the upstream loader wrapped
 * everything in BUI `<Container>` with no padding), but we now ship a
 * custom /docs page in `packages/app/src/modules/techdocs/index.tsx`
 * that wraps in `<Content>` directly. NEVER add a path here whose
 * loader already returns `<Content>` — you'll end up with double
 * padding.
 */
const PAGES_NEEDING_CONTENT_PADDING: readonly string[] = [];

/**
 * Bosch override of the framework's default `PageLayout`.
 *
 * Visual contract — matches the chrome rendered by `<Page><Header/>`
 * from `@backstage/core-components` (the one `KindsGuidePage` and
 * `PermissionsGuidePage` use), so every top-level page in the app
 * looks the same regardless of who authored the loader:
 *
 *   - 24px-padded blue `<header>` with `boxShadow: theme.shadows[4]`.
 *   - Title rendered with `variant="h1"` (so it inherits theme weight
 *     + boschWhite via the `BackstageHeader` style overrides we ship),
 *     sized at h3 to match `BackstageHeader`'s `theme.typography.h3`.
 *   - Optional subtitle in `subtitle2` with `opacity: 0.8`, sourced
 *     from the `PAGE_OVERRIDES` map keyed on URL prefix.
 *
 * Pages whose loader brings its own header (catalog/apis/rbac → see
 * `PAGES_WITH_OWN_HEADER`) skip our banner so we don't stack two on
 * top of each other.
 *
 * Tabs are rendered as react-router `<Link>`s (NOT plain `<a>`s) so
 * navigation stays client-side and the auth/identity state survives —
 * the framework default uses `<a href>` and bounces the user back to
 * `/sign-in` every time you click a tab.
 *
 * Registered via `SwappableComponentBlueprint`, which only accepts
 * `pluginId: 'app'`. We attach to the existing `themeModule`.
 */

function PageLayoutTabLink({ tab }: { tab: PageLayoutTab }) {
  const resolved = useResolvedPath(tab.href);
  const { pathname } = useLocation();
  const active =
    pathname === resolved.pathname ||
    pathname.startsWith(`${resolved.pathname}/`);

  return (
    <Link
      to={tab.href}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '12px 14px',
        textDecoration: 'none',
        color: active ? boschBlue[50] : boschGray[30],
        borderBottom: `2px solid ${active ? boschBlue[50] : 'transparent'}`,
        fontWeight: 600,
        fontSize: '0.85rem',
      }}
    >
      {tab.icon}
      {tab.label}
    </Link>
  );
}

/** Looks up the override that best matches the current URL.
 *
 *  We support an exact match OR a prefix match so `/create/templates`
 *  inherits the override for `/create`, `/docs/default/component/foo`
 *  inherits `/docs`, etc. Returns `undefined` when no entry matches —
 *  callers fall back to whatever title the framework gave us. */
function findPageOverride(pathname: string) {
  if (PAGE_OVERRIDES[pathname]) {
    return PAGE_OVERRIDES[pathname];
  }
  for (const prefix of Object.keys(PAGE_OVERRIDES)) {
    if (pathname.startsWith(`${prefix}/`)) {
      return PAGE_OVERRIDES[prefix];
    }
  }
  return undefined;
}

function BoschPageLayout(props: PageLayoutProps) {
  const { title, noHeader, headerActions, tabs, children } = props;
  const { pathname } = useLocation();
  // Side-effect hook — emits the @global CSS rules defined above on
  // first render. The empty `_` we throw away here matches the way
  // makeStyles is used elsewhere when only `@global` rules are needed
  // (no per-element class names to consume).
  useGlobalChromeStyles();

  const onPageWithOwnHeader = PAGES_WITH_OWN_HEADER.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  const override = findPageOverride(pathname);
  const displayTitle = override?.title ?? title;
  const subtitle = override?.subtitle;

  const showBanner =
    !noHeader && Boolean(displayTitle) && !onPageWithOwnHeader;

  const needsExtraPadding = PAGES_NEEDING_CONTENT_PADDING.some(
    prefix => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );

  // The upstream `@backstage-community/plugin-rbac` plugin hardcodes
  // `<Page><Header title="RBAC" /><Content /></Page>` inside its
  // `RbacPage` component (see node_modules/@backstage-community/plugin-rbac/
  // dist/components/RbacPage.esm.js). It accepts a `useHeader` prop on
  // `Router`, but the alpha plugin's PageBlueprint loader calls
  // `<Router />` with no props — and Router/RbacPage are not exported
  // through the package's `exports` field, so we can't override the
  // loader from outside. Result: visiting /rbac stacks our blue Bosch
  // banner ON TOP of the white "RBAC" header from the plugin.
  //
  // Fix: tag the wrapper with a className so the JSS rule we ship in
  // theme/index.tsx (`BackstageHeader.styleOverrides.header` →
  // `'.bosch-suppress-upstream-header &': { display: 'none' }`) hides
  // the upstream `<Header>` whenever it lands inside our /rbac
  // wrapper. Our own blue banner above is plain inline-styled
  // `<header>` and does NOT carry a `BackstageHeader-header-XXX`
  // class, so the rule only kills the upstream chrome — no risk of
  // hiding our own.
  const suppressUpstreamHeader =
    pathname === '/rbac' || pathname.startsWith('/rbac/');

  return (
    <div
      data-component="page-layout"
      className={
        suppressUpstreamHeader ? 'bosch-suppress-upstream-header' : undefined
      }
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        minHeight: 0,
      }}
    >
      {showBanner && (
        <header
          style={{
            backgroundColor: boschBlue[50],
            color: boschWhite,
            borderBottom: `1px solid ${boschBlue[40]}`,
            padding: '24px',
            boxShadow:
              '0px 2px 4px -1px rgba(0,0,0,0.06), 0px 4px 5px 0px rgba(0,0,0,0.04)',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 16,
              maxWidth: '100%',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h1"
                style={{
                  margin: 0,
                  fontSize: '1.875rem',
                  fontWeight: 600,
                  lineHeight: 1.2,
                  color: boschWhite,
                  wordBreak: 'break-word',
                }}
              >
                {displayTitle}
              </Typography>
              {subtitle && (
                <Typography
                  variant="subtitle2"
                  component="span"
                  style={{
                    display: 'inline-block',
                    marginTop: 8,
                    color: boschWhite,
                    opacity: 0.8,
                    maxWidth: '75ch',
                    fontWeight: 400,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </div>
            {headerActions && headerActions.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                {headerActions}
              </div>
            )}
          </div>
        </header>
      )}

      {tabs && tabs.length > 0 && (
        <nav
          style={{
            display: 'flex',
            gap: 4,
            padding: '0 24px',
            backgroundColor: boschWhite,
            borderBottom: `1px solid ${boschGray[90]}`,
            flexShrink: 0,
          }}
        >
          {tabs.map(tab => (
            <PageLayoutTabLink key={tab.id} tab={tab} />
          ))}
        </nav>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          minHeight: 0,
          ...(needsExtraPadding ? { padding: '0 24px' } : {}),
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Wires `BoschPageLayout` in as the implementation behind the framework's
 *  `PageLayout` swappable component. The blueprint's `attachTo` resolves
 *  to `api:app/swappable-components`, so this only works when registered
 *  under `pluginId: 'app'` (enforced by the framework).
 *
 *  Note the `defineParams` callback form below: SwappableComponentBlueprint
 *  is a generic-typed blueprint (its `component` and `loader` types are
 *  inferred from each other through the `SwappableComponentRef`). The
 *  framework requires the callback form so it can do that inference —
 *  passing a plain `params: { ... }` object throws at app startup with
 *  `EXTENSION_FACTORY_ERROR: ... requires them to be passed in callback form`. */
export const boschPageLayoutExtension = SwappableComponentBlueprint.make({
  name: 'bosch-page-layout',
  params: defineParams =>
    defineParams({
      component: PageLayout,
      loader: () => BoschPageLayout,
    }),
});
